'use client';

import { useCallback, useState } from 'react';

import type { Provider } from '@supabase/supabase-js';

import { isBrowser, isSafeRedirectPath } from '@kit/shared/utils';
import { If } from '@kit/ui/if';
import { Separator } from '@kit/ui/separator';
import { Trans } from '@kit/ui/trans';

import { MagicLinkAuthContainer } from './magic-link-auth-container';
import { OauthProviders } from './oauth-providers';
import { OtpSignInContainer } from './otp-sign-in-container';
import { EmailPasswordSignUpContainer } from './password-sign-up-container';

export function SignUpMethodsContainer(props: {
  paths: {
    callback: string;
    appHome: string;
  };

  providers: {
    password: boolean;
    magicLink: boolean;
    otp: boolean;
    oAuth: Provider[];
  };

  displayTermsCheckbox?: boolean;
  captchaSiteKey?: string;

  /** Bubbles the "check your email" state up so the page can swap its own copy. */
  onVerificationChange?: (verifying: boolean) => void;
}) {
  const redirectUrl = getCallbackUrl(props);
  const defaultValues = getDefaultValues();

  // Once the confirmation email is out, every other way to sign up is a dead
  // end — the account already exists. Offering "Continue with Google" next to
  // it invited people to start a second, different account.
  const [verifying, setVerifying] = useState(false);
  const { onVerificationChange } = props;

  const handleVerificationChange = useCallback(
    (next: boolean) => {
      setVerifying(next);
      onVerificationChange?.(next);
    },
    [onVerificationChange],
  );

  // Every branch below stays in place while verifying — the password
  // container holds the submitted address in state, and unmounting it to swap
  // layouts would throw that away.
  return (
    <>
      <If condition={!verifying && props.providers.oAuth.length}>
        <OauthProviders
          enabledProviders={props.providers.oAuth}
          shouldCreateUser={true}
          paths={{
            callback: props.paths.callback,
            returnPath: props.paths.appHome,
          }}
        />
      </If>

      <If
        condition={
          !verifying &&
          props.providers.oAuth.length > 0 &&
          (props.providers.magicLink ||
            props.providers.password ||
            props.providers.otp)
        }
      >
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator />
          </div>

          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">
              <Trans i18nKey="auth:orContinueWith" />
            </span>
          </div>
        </div>
      </If>

      <If condition={props.providers.password}>
        <EmailPasswordSignUpContainer
          emailRedirectTo={redirectUrl}
          defaultValues={defaultValues}
          displayTermsCheckbox={props.displayTermsCheckbox}
          captchaSiteKey={props.captchaSiteKey}
          onVerificationChange={handleVerificationChange}
        />
      </If>

      <If condition={!verifying && props.providers.otp}>
        <OtpSignInContainer
          shouldCreateUser={true}
          captchaSiteKey={props.captchaSiteKey}
        />
      </If>

      <If condition={!verifying && props.providers.magicLink}>
        <MagicLinkAuthContainer
          redirectUrl={redirectUrl}
          shouldCreateUser={true}
          defaultValues={defaultValues}
          displayTermsCheckbox={props.displayTermsCheckbox}
          captchaSiteKey={props.captchaSiteKey}
        />
      </If>
    </>
  );
}

function getCallbackUrl(props: {
  paths: {
    callback: string;
    appHome: string;
  };
}) {
  if (!isBrowser()) {
    return '';
  }

  const redirectPath = props.paths.callback;
  const origin = window.location.origin;
  const url = new URL(redirectPath, origin);

  const searchParams = new URLSearchParams(window.location.search);
  const next = searchParams.get('next');
  const returnPath =
    next && isSafeRedirectPath(next) ? next : props.paths.appHome;

  // Preserve a safe explicit destination, otherwise use the sign-up flow's
  // default. This makes every sign-up method carry its destination through the
  // auth callback instead of relying on the callback's generic fallback.
  if (isSafeRedirectPath(returnPath)) {
    url.searchParams.set('next', returnPath);
  }

  return url.href;
}

function getDefaultValues() {
  if (!isBrowser()) {
    return { email: '' };
  }

  const searchParams = new URLSearchParams(window.location.search);

  return {
    email: searchParams.get('email') ?? '',
  };
}
